import { useEffect } from "react";
import styled from "styled-components";
import { useRecoilValue } from "recoil";
import { useParams } from "react-router-dom";

import COLORS from "components/atoms/colors";
import { H2, H4 } from "components/atoms/text";
import Btn from "components/atoms/buttons";
import SelectSize from "components/templates/project/SideBar/LiveProduct/SelectSize";
import SHADOWS from "components/atoms/shadows";
import Image from "components/organisms/Image";
import Line from "components/atoms/line";

import productTitles from "datas/productTitles";

import useProduct from "apis/queries/product/useProduct";
import useDeletePin from "apis/mutations/pin/useDeletePin";

import authAtom from "stores/auth";

import { ReactComponent as IconClose } from "assets/images/common/close.svg";
import { ReactComponent as IconLink } from "assets/images/project/link.svg";

interface Props {
  productId: number;
  productOptionId: number;
  pinId: number;
  onChangeProductOptionId: (optionId: number) => void;
  onClose: () => void;
  onChageSceneModePin: () => void;
}

const SCHEMAS = ["http://", "https://"];

// 상품 상세
const Detail = ({
  productId,
  productOptionId,
  pinId,
  onChangeProductOptionId,
  onClose,
  onChageSceneModePin,
}: Props) => {
  const params = useParams();
  const auth = useRecoilValue(authAtom);

  const projectId = Number(params.id);

  // 핀 삭제 api
  const deletePin = useDeletePin({
    projectId,
    option: { onSuccess: () => onClose() },
  });

  // 상품 상세 api
  const product = useProduct({ productId });

  // 상품 옵션 아이디 저장
  useEffect(() => {
    if (product) {
      onChangeProductOptionId(product.options[0]?.id);
    }
  }, [product]);

  if (!product) return null;

  const {
    subCategory,
    name,
    code,
    category,
    options,
    manufacturer,
    origin,
    feature,
    isShowPrice,
    glossiness,
    image,
    url,
    country,
  } = product;

  const isCompanyUser = auth.type === "company";
  const currentOption = options.find(({ id }) => id === productOptionId);

  const _url = SCHEMAS.some((schma) => !!url?.includes(schma))
    ? url
    : `https://${url}`;

  return (
    <Container>
      <div className="flex-none px-5 pt-5">
        <div className="flex items-center justify-between h-[34px]">
          <H2 className="text-primary-active">Live product</H2>
          <button onClick={onClose}>
            <IconClose />
          </button>
        </div>
        <ImageWrap>
          {url && _url && (
            <BtnLink href={_url} target="_blank">
              <IconLink />
              <span>{url}</span>
            </BtnLink>
          )}
          <Image src={image} className="!h-auto" />
        </ImageWrap>
      </div>

      <div className="flex-1 px-10 overflow-y-auto">
        <ul>
          {productTitles.map(({ title, enTitle }) => {
            const content = {
              제품명: name,
              상품코드: code,
              구분: category?.name,
              종류: subCategory,
              제조사: manufacturer,
              원산지: origin === "수입산" ? country : origin,
              광택도: glossiness,
              사이즈: (
                <SelectSize
                  options={options}
                  onChange={onChangeProductOptionId}
                />
              ),
            };

            return (
              <Item key={title}>
                <div className="flex-none flex items-center gap-2">
                  <p className="font-bold">{title}</p>
                  <p className="text-black-60%">{enTitle}</p>
                </div>
                <div className="text-right">
                  {content[title as keyof typeof content]}
                </div>
              </Item>
            );
          })}
        </ul>

        <div className="flex-none flex items-center gap-2">
          <p className="font-bold">특징</p>
          <p className="text-black-60%">Feature</p>
        </div>
        <FeatureWrap>
          <p>{feature}</p>
        </FeatureWrap>
      </div>

      <div className="flex-none px-10 py-5">
        <Line background="#D2D2D2" className="mb-5" />

        <div className="flex justify-between mt-5">
          <H4>단가(m2)</H4>
          <div>
            <H4
              className={`text-${isCompanyUser ? "black-100%" : "black-60%"}`}
            >
              {currentOption &&
                (() => {
                  const isPrice =
                    auth.type === "user" ||
                    currentOption.price === 0 ||
                    !isShowPrice;

                  if (isPrice) return "가격문의";
                  return `￦${currentOption.price.toLocaleString()}`;
                })()}
            </H4>
          </div>
        </div>
        <p className="my-5 text-black-60% break-keep text-[12px]">
          {auth.type === "user"
            ? "*모든 제품의 단가는 ‘전문가 회원’만 열람할 수 있습니다."
            : "*상기 제품의 단가는 사용자 검색 기준을 위해 표기된 설계 단가이며 샘플 신청시 기입한 면적에 따라 단가는 상이할 수 있습니다."}
        </p>

        {pinId ? (
          <Btn.Red
            onClick={() => deletePin([pinId])}
            size="large"
            className="w-full"
          >
            샘플 삭제
          </Btn.Red>
        ) : (
          <Btn.Blue
            onClick={onChageSceneModePin}
            size="large"
            className="w-full !text-sm"
            radiusFull
          >
            관심 샘플 등록
          </Btn.Blue>
        )}
      </div>
    </Container>
  );
};

const Item = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 8px;
`;

const Container = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: 100%;
  background-color: ${COLORS.white["60%"]};
  display: flex;
  flex-direction: column;
`;

const ImageWrap = styled.div`
  position: relative;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 40px 20px;
  height: 260px;
  border-radius: 10px;
  overflow: hidden;
`;

const BtnLink = styled.a`
  position: absolute;
  left: 12px;
  bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: #fff;
  width: 120px;
  padding: 8px 12px;
  border-radius: 19px;
  box-shadow: ${SHADOWS.button};

  & > svg {
    flex: none;
  }

  & > span {
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const FeatureWrap = styled.div`
  margin-top: 8px;
  word-break: break-all;
`;

export default Detail;
